import { create } from "zustand";
import { persist } from "zustand/middleware";
import { TModelType, DEFAULT_CHOOSER_MODEL, isChooserModel } from "../shared/types/models";
import { LS_ZAPI_CHAT_HISTORY_STORAGE } from "../shared/local-storage/keys";
import { createChatHistorySlice, TChatHistorySlice } from "./lib/createChatHistorySlice";

export type ChatMessage = {
  role: "user" | "assistant";
  content: string;
  id: string;
};

type TChatKey = `${string}:${string}`;

type ChatLoadingState = {
  loadingMessageId: string | null;
  pendingBotMessage: ChatMessage | null;
  pendingGenerationId: number | null;
  isGeneratingChapter: boolean;
  isGeneratingExecutiveSummary: boolean;
  progressMessage: string | null;
  prereqsChaptersToBeGenerated: string[] | null;
  allPrereqChapters: string[] | null;
  abortController: AbortController | null;
  lastUserMessageRef: { projectId: string; chapterId: string; messageId: string } | null;
  isChapterLoaded?: boolean;
};

export type ChatLoadingStateWithId = ChatLoadingState & {
  chapterId: string;
};

interface ChatState {
  modelName: TModelType;
  pendingChanges: Record<string, boolean>;
  chatHistory: Record<string, ChatMessage[]>;
  loadingStates: Record<string, ChatLoadingState>;
  getChatHistory: (projectId: string, chapterId: string) => ChatMessage[];
  addMessage: (projectId: string, chapterId: string, message: ChatMessage) => void;
  clearChatHistory: (projectId: string, chapterId: string) => void;
  removeLastMessage: (projectId: string, chapterId: string) => void;

  getMessagesWithPending: (projectId: string, chapterId: string) => ChatMessage[];

  updatePendingMessage: (projectId: string, chapterId: string, content: string) => void;

  getLoadingState: (projectId: string, chapterId: string) => ChatLoadingState;
  setLoadingState: (projectId: string, chapterId: string, updates: Partial<ChatLoadingState>) => void;
  resetLoadingState: (projectId: string, chapterId: string) => void;
  markChapterAsLoaded: (chapterId: number) => void;

  setModelName: (modelName: TModelType) => void;
}

const createChatKey = (projectId: string, chapterId: string): TChatKey => {
  return `${projectId}:${chapterId}`;
};

export const isChapterGenerating = (loadingState: ChatLoadingState) =>
  (!!loadingState.pendingGenerationId || loadingState.isGeneratingChapter) && !loadingState.isChapterLoaded;

export const selectIsChapterGenerating = (projectId: string, chapterId: string) => (state: ChatState) => {
  const loadingState = state.loadingStates[createChatKey(projectId, chapterId)];
  return !!loadingState && isChapterGenerating(loadingState);
};

const createDefaultLoadingState = (): ChatLoadingState => ({
  loadingMessageId: null,
  pendingBotMessage: null,
  isGeneratingChapter: false,
  isGeneratingExecutiveSummary: false,
  progressMessage: null,
  prereqsChaptersToBeGenerated: null,
  allPrereqChapters: null,
  abortController: null,
  lastUserMessageRef: null,
  pendingGenerationId: null
});

export const useChatStore = create<ChatState>()(
  persist(
    (set, get) => {
      const slice: TChatHistorySlice<ChatMessage, ChatLoadingState> = createChatHistorySlice(
        set,
        get,
        createDefaultLoadingState
      );

      return {
        ...slice,
        modelName: DEFAULT_CHOOSER_MODEL,
        pendingChanges: {},

        getChatHistory: (projectId, chapterId) => slice.getChatHistory(createChatKey(projectId, chapterId)),
        addMessage: (projectId, chapterId, message) => slice.addMessage(createChatKey(projectId, chapterId), message),
        clearChatHistory: (projectId, chapterId) => slice.clearChatHistory(createChatKey(projectId, chapterId)),
        removeLastMessage: (projectId, chapterId) => slice.removeLastMessage(createChatKey(projectId, chapterId)),
        getLoadingState: (projectId, chapterId) => slice.getLoadingState(createChatKey(projectId, chapterId)),
        setLoadingState: (projectId, chapterId, updates) =>
          slice.setLoadingState(createChatKey(projectId, chapterId), updates),
        resetLoadingState: (projectId, chapterId) => slice.resetLoadingState(createChatKey(projectId, chapterId)),
        getMessagesWithPending: (projectId, chapterId) =>
          slice.getMessagesWithPending(createChatKey(projectId, chapterId)),

        markChapterAsLoaded: (chapterId: number) => {
          set((state) => {
            const chapterIdStr = String(chapterId);
            const updatedLoadingStates: Record<string, ChatLoadingState> = {};

            Object.entries(state.loadingStates).forEach(([key, loadingState]) => {
              const [, keyChapterId] = key.split(":");
              updatedLoadingStates[key] =
                keyChapterId === chapterIdStr ? { ...loadingState, isChapterLoaded: true } : loadingState;
            });

            return { loadingStates: updatedLoadingStates };
          });
        },

        updatePendingMessage: (projectId, chapterId, content) => {
          set((state) => {
            const key = createChatKey(projectId, chapterId);
            const currentState = state.loadingStates[key] || createDefaultLoadingState();

            if (!currentState.pendingBotMessage) {
              return state;
            }

            return {
              loadingStates: {
                ...state.loadingStates,
                [key]: {
                  ...currentState,
                  pendingBotMessage: { ...currentState.pendingBotMessage, content: content }
                }
              }
            };
          });
        },

        setModelName: (modelName: TModelType) => set({ modelName })
      };
    },
    {
      name: LS_ZAPI_CHAT_HISTORY_STORAGE,
      skipHydration: true,
      merge: (persisted, current) => {
        const merged = { ...current, ...(persisted as object) } as ChatState;
        if (!isChooserModel(merged.modelName)) {
          merged.modelName = DEFAULT_CHOOSER_MODEL;
        }
        return merged;
      },
      partialize: (state) => ({
        modelName: state.modelName,
        chatHistory: state.chatHistory,
        loadingStates: Object.fromEntries(
          Object.entries(state.loadingStates).map(([key, value]) => [
            key,
            {
              ...value,
              abortController: null
            }
          ])
        )
      })
    }
  )
);
